import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { formatInTimeZone } from 'date-fns-tz';

import { EvChargerData, GenericStatusResponse } from 'clipsal-cortex-types/src/api/api-evcharger';

import { RootState } from '../../../app/store';
import { NetworkError, post } from '../../../common/api/api-helpers';
import { MANUFACTURER_ID_SCHNEIDER } from '../../devices/devices-helper';
import { addEvCharger } from '../../site/siteSlice';
import { addEnrolledEvChargerDetails } from '../dashboard/evChargerSlice';
import { QRCodeValues } from '../types';

export type DeviceSetupState = {
  qrCodeValues: QRCodeValues | null;
  status: GenericStatusResponse['status'];
  message: string;
  isLoaded: boolean;
  hasInitiatedEnroll: boolean;
};

export const enrolEvCharger = createAsyncThunk<GenericStatusResponse, QRCodeValues, { state: RootState }>(
  'site/enrolEvCharger',
  async ({ sn: serialNumber, cpid: chargerId }, { getState, dispatch }) => {
    // Charger QR scanning gives the following attributes:
    // sn = Serial number of the charger
    // cpid = ID of the charger

    try {
      const state = getState();
      const siteID = state.site.site_id;
      const manufacturer = 'Schneider Electric';
      const body = {
        charger_id: chargerId,
        serial_number: serialNumber,
        manufacturer_name: manufacturer,
      };
      const response = await post<EvChargerData>(`/v1/sites/${siteID}/ev_chargers`, body);
      // Add the charger to the site.
      dispatch(
        addEvCharger({
          id: response.site_device_id,
          device_type: 'EV_CHARGER',
          manufacturer_id: MANUFACTURER_ID_SCHNEIDER,
          manufacturer_name: manufacturer,
          manufacturer,
          model: '',
          created: formatInTimeZone(new Date(), state.site.timezone, 'yyyy-MM-dd'),
          site_identifier: chargerId,
          serial_number: serialNumber,
          operating_state: null,
          is_hybrid_inverter: false,
          ja12_compliance: false,
        })
      );

      // Need to pull fresh data from the server as the redux store is not updated yet.
      dispatch(addEnrolledEvChargerDetails(response));

      return { status: 'success' };
    } catch (error) {
      const response = ((<AxiosError>error) as NetworkError)?.response;

      // For specific 409 errors, we want to show a different message based on the detail.
      if (response?.status === 409 && response?.data.detail) {
        return { status: 'fail', message: response.data.detail };
      }

      const message = response?.data.message || '';
      return { status: 'fail', message };
    }
  }
);

const initialState: DeviceSetupState = {
  isLoaded: false,
  qrCodeValues: null,
  hasInitiatedEnroll: false,
  status: 'success',
  message: '',
};

export const deviceSetupSlice = createSlice({
  name: 'deviceSetup',
  initialState,
  reducers: {
    resetEnrollState: (state) => {
      return { ...state, hasInitiatedEnroll: false };
    },
    setQRCodeValues: (state, action: PayloadAction<QRCodeValues | null>) => {
      state.qrCodeValues = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(enrolEvCharger.pending, (state) => {
      return { ...state, hasInitiatedEnroll: true, isLoaded: false };
    });
    builder.addCase(enrolEvCharger.fulfilled, (state, { payload }) => {
      return { ...state, ...payload, isLoaded: true };
    });
    builder.addCase(enrolEvCharger.rejected, (state) => {
      return { ...state, status: 'fail', isLoaded: true };
    });
  },
});

export const { resetEnrollState, setQRCodeValues } = deviceSetupSlice.actions;

export const selectDeviceSetupState = (state: RootState) => {
  return state.deviceSetup;
};

export const { reducer: deviceSetupReducer } = deviceSetupSlice;
